import { useMsal } from '@azure/msal-react';
import BlockIcon from '@mui/icons-material/Block';
import KeyOffIcon from '@mui/icons-material/KeyOff';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import { Alert, Box, Button, Card, CardContent, Container, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation } from 'react-router';
import { ApiError, errorCodes } from '../api/http';
import { Logo } from '../components/Logo';
import { LoadingBlock } from '../components/QueryStateOverlay';
import { useDescribeError } from '../i18n/useDescribeError';
import { useCallerContextQuery, useIsAdministrator } from './useCallerContext';
import { useSignedInUser } from './useSignedInUser';

/**
 * Which dead end the caller reached. They are told apart deliberately: only one of the three is
 * fixed in this service's own administration, and sending somebody to the wrong department is the
 * whole failure mode these screens exist to avoid.
 */
type RefusalKind = 'notRegistered' | 'disabled' | 'noRole';

interface RefusedProps {
  kind: RefusalKind;
  isAdministrator: boolean;
}

const headingKeys = {
  notRegistered: 'auth.notRegisteredTitle',
  disabled: 'auth.disabledTitle',
  noRole: 'auth.noRoleTitle',
} as const satisfies Record<RefusalKind, string>;

const explanationKeys = {
  notRegistered: 'auth.notRegisteredBody',
  disabled: 'auth.disabledBody',
  noRole: 'auth.noRoleBody',
} as const satisfies Record<RefusalKind, string>;

function Refused({ kind, isAdministrator }: RefusedProps) {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const user = useSignedInUser();

  const signedInName = user.displayName ?? user.username ?? t('auth.unknownUser');
  const signedInSuffix = user.username && user.displayName ? ` (${user.username})` : '';

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Logo width={220} height="auto" />
      </Box>
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {kind === 'disabled' ? (
                <BlockIcon color="error" />
              ) : kind === 'noRole' ? (
                <KeyOffIcon color="warning" />
              ) : (
                <PersonOffIcon color="warning" />
              )}
              <Typography variant="h6">{t(headingKeys[kind])}</Typography>
            </Box>

            <Typography color="text.secondary">{t(explanationKeys[kind])}</Typography>

            {/* The tenant roles are granted in the directory, so this screen points there rather
                than at anything in this console. */}
            {kind === 'noRole' && (
              <Typography color="text.secondary">
                <Trans i18nKey="auth.noRoleHint" components={{ strong: <strong /> }} />
              </Typography>
            )}

            {/* The token names tenants this service does not know: the fix is registering a tenant
                under the very code the role carries, which only an administrator can do. */}
            {kind === 'notRegistered' && (
              <Typography color="text.secondary">
                <Trans i18nKey="auth.notRegisteredHint" components={{ strong: <strong /> }} />
              </Typography>
            )}

            <Typography variant="body2" color="text.secondary">
              {t('auth.signedInAs', { name: `${signedInName}${signedInSuffix}` })}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => void instance.logoutRedirect()}>{t('actions.signOut')}</Button>
              {/* Administration answers without a resolved tenant, so it stays reachable — it is
                  where the first tenant and the first user get registered. Offering it to anyone
                  without the role would only lead them to a second refusal. */}
              {isAdministrator && (
                <Button component={RouterLink} to="/admin">
                  {t('nav.administration')}
                </Button>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
}

/** The API's three dead ends, each with a screen of its own. */
const refusalKinds: Record<string, RefusalKind> = {
  [errorCodes.tenantNotRegistered]: 'notRegistered',
  [errorCodes.tenantDisabled]: 'disabled',
  [errorCodes.roleNotAssigned]: 'noRole',
};

/**
 * Establishes who the caller is before anything else renders. Only the API's own refusals — no role,
 * not registered, every tenant out of service — are dead ends; being registered with no tenant
 * assigned yet answers 200 with an empty list and belongs in the console, not on these screens.
 */
export function TenantGate({ children }: { children: ReactNode }) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const describeError = useDescribeError();
  const location = useLocation();

  const query = useCallerContextQuery();
  const isAdministrator = useIsAdministrator();

  // The administration endpoints skip tenant resolution server-side, precisely so the registry can
  // be populated before anyone belongs to a tenant. Gating that section here would deadlock setup —
  // but only somebody holding the role gets past /api/admin, so nobody else is let through to it.
  const isAdministration = location.pathname.startsWith('/admin') && isAdministrator;

  const errorCode = query.error instanceof ApiError ? query.error.errorCode : undefined;
  const refusal =
    query.error instanceof ApiError && query.error.status === 403 && errorCode !== undefined
      ? refusalKinds[errorCode]
      : undefined;

  useEffect(() => {
    if (query.error && refusal === undefined) {
      enqueueSnackbar(describeError(query.error), { variant: 'error' });
    }
  }, [query.error, refusal, enqueueSnackbar, describeError]);

  if (query.isPending) return <LoadingBlock label={t('auth.checkingRegistration')} />;

  if (refusal !== undefined && !isAdministration) {
    return <Refused kind={refusal} isAdministrator={isAdministrator} />;
  }

  if (query.isError && !isAdministration) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void query.refetch()}>
              {t('actions.retry')}
            </Button>
          }
        >
          {describeError(query.error)}
        </Alert>
      </Container>
    );
  }

  return <>{children}</>;
}
